// Story 013 — finding/defect selection + activation store. Single
// source of truth for the cross-widget cascade. The Tiles widget
// publishes; the Viewer + FindingsPanel + (future) Defect List
// subscribe. Multi-select state is also here so Story 016+'s batch-
// ops surface can read from the same store without a refactor.
import { create } from "zustand";

/**
 * R58-L-13 — kind hint for the active selection so the List widget can
 * switch tabs when a Viewer-bbox click lands on a defect overlay. Kept
 * optional (defaults to `null`) so 013-era callers that only set the
 * id still compile.
 */
export type SelectionKind = "ai" | "defect";

export interface SelectionStoreState {
  /** The "current" finding/defect — Viewer highlights its BBox; FindingsPanel scrolls to its row. */
  activeFindingId: string | null;
  /** R58-L-13 — kind discriminator. `null` when no selection or when the
   *  caller can't determine the kind (legacy paths). The reverse-direction
   *  effect uses this to decide which List tab to activate + which row
   *  to scroll into view. */
  activeKind: SelectionKind | null;
  /** Set of findingIds currently selected. Always contains activeFindingId
   *  when activeFindingId is non-null. Used by future batch-ops surfaces
   *  (Story 016+) — for now, mostly a single-element set. */
  selectedFindingIds: Set<string>;
  /** Anchor for shift-click ranges. The most recently single-clicked
   *  tile. */
  anchorFindingId: string | null;

  setActive: (findingId: string | null, kind?: SelectionKind | null) => void;
  /** Replace selection with a single id and set it as the active +
   *  anchor. Used on plain click. TRIAGE-01 — the optional `kind` lets
   *  the keyboard triage cursor stamp `activeKind: "ai"` in the same
   *  write (legacy callers omit it → `null`, unchanged). */
  selectOne: (findingId: string, kind?: SelectionKind | null) => void;
  /** Toggle membership in the selection set without changing the anchor.
   *  Used on Ctrl/Cmd-click. The toggled id becomes (or stays) active. */
  toggleSelection: (findingId: string) => void;
  /** Replace selection with the contiguous range between the current
   *  anchor (or the passed `from` if no anchor) and the clicked id.
   *  `allIds` is the visible-order list of finding ids the caller
   *  derives from the active image. */
  selectRange: (toFindingId: string, allIds: string[]) => void;
  reset: () => void;
}

function spread(set: Set<string>): Set<string> {
  return new Set(set);
}

export const useSelectionStore = create<SelectionStoreState>((set, get) => ({
  activeFindingId: null,
  activeKind: null,
  selectedFindingIds: new Set<string>(),
  anchorFindingId: null,

  setActive: (findingId, kind = null) => {
    set({ activeFindingId: findingId, activeKind: kind });
  },

  selectOne: (findingId, kind = null) => {
    set({
      activeFindingId: findingId,
      activeKind: kind,
      selectedFindingIds: new Set([findingId]),
      anchorFindingId: findingId,
    });
  },

  toggleSelection: (findingId) => {
    const cur = get().selectedFindingIds;
    const next = spread(cur);
    if (next.has(findingId)) {
      next.delete(findingId);
    } else {
      next.add(findingId);
    }
    set({
      selectedFindingIds: next,
      activeFindingId: findingId,
    });
  },

  selectRange: (toFindingId, allIds) => {
    const anchor = get().anchorFindingId ?? toFindingId;
    const fromIdx = allIds.indexOf(anchor);
    const toIdx = allIds.indexOf(toFindingId);
    if (fromIdx === -1 || toIdx === -1) {
      // Fallback to single-select if the anchor isn't in the visible list.
      get().selectOne(toFindingId);
      return;
    }
    const [lo, hi] = fromIdx <= toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
    const range = allIds.slice(lo, hi + 1);
    set({
      selectedFindingIds: new Set(range),
      activeFindingId: toFindingId,
    });
  },

  reset: () => {
    set({
      activeFindingId: null,
      activeKind: null,
      selectedFindingIds: new Set<string>(),
      anchorFindingId: null,
    });
  },
}));

// R58-L-11 / SB5-C.7 — Playwright test seam. The bulk-dismiss AC3 test
// needs to populate the multi-select set without going through the
// Tiles widget (the panel-scoped spec doesn't render Tiles). Exposing
// the store on `window` lets `page.evaluate(...)` set the slice
// directly. Skipped on SSR; harmless on the production bundle (the
// Zustand store is already a singleton object — pointing window at it
// adds no overhead).
interface WindowWithSelectionStore {
  __wpSelectionStore?: typeof useSelectionStore;
}
if (typeof window !== "undefined") {
  (window as unknown as WindowWithSelectionStore).__wpSelectionStore =
    useSelectionStore;
}
